import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, getImageSrc } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { ShareButtons } from "@/components/ShareButtons";
import { MapPin, Package, ShieldCheck, Store, Minus, Plus, MessageCircle, Tag, Lock } from "lucide-react";

const toIntlPhone = (phone) => {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.startsWith("233")) return digits;
  if (digits.startsWith("0")) return "233" + digits.slice(1);
  return digits;
};

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => setP(false));
  }, [id]);

  if (p === null) return <div className="p-10 text-center text-[#5C6660]">Loading...</div>;
  if (p === false) return <div className="p-10 text-center">Product not found. <Link className="text-[#C85A32]" to="/marketplace">Back to marketplace</Link></div>;

  const tiers = (p.bulk_tiers || []).slice().sort((a, b) => a.min_qty - b.min_qty);
  const effectivePrice = () => {
    let best = p.price;
    tiers.forEach((t) => { if (qty >= t.min_qty && t.price < best) best = t.price; });
    return best;
  };
  const currentPrice = effectivePrice();
  const savings = ((p.price - currentPrice) * qty).toFixed(2);

  const handleAdd = async () => {
    const r = await addItem(p.id, qty);
    if (!r?.ok) nav("/login");
  };

  const handleBuy = async () => {
    const r = await addItem(p.id, qty);
    if (r?.ok) nav("/checkout");
    else nav("/login");
  };

  const whatsappUrl = p.seller_phone
    ? `https://wa.me/${toIntlPhone(p.seller_phone)}?text=${encodeURIComponent(`Hi ${p.seller_name}, I'm interested in your listing "${p.name}" on AgriNuhse. Is it still available?`)}`
    : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="text-xs text-[#5C6660] mb-4">
        <Link to="/marketplace" className="hover:text-[#C85A32]">Marketplace</Link> · <Link to={`/marketplace?category=${p.category}`} className="hover:text-[#C85A32] capitalize">{p.category}</Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <div className="card-farm overflow-hidden self-start min-w-0">
          <img src={getImageSrc(p)} alt={p.name} className="w-full h-[440px] object-cover" data-testid="product-image" />
        </div>
        <div className="min-w-0">
          <span className="badge-cat">{p.category}</span>
          <h1 className="text-3xl sm:text-4xl font-black text-[#1F2421] mt-3">{p.name}</h1>
          <div className="flex items-center gap-2 mt-2 text-sm text-[#5C6660]">
            <MapPin className="w-4 h-4" /> {p.region}
          </div>

          <div className="mt-6 flex items-baseline gap-3">
            <div className="text-4xl font-black text-[#C85A32]" data-testid="product-price">GH₵ {currentPrice.toFixed(2)}</div>
            {currentPrice < p.price && (
              <div className="text-sm text-[#5C6660] line-through">GH₵ {p.price.toFixed(2)}</div>
            )}
            <div className="text-sm text-[#5C6660]">per {p.unit}</div>
          </div>

          <p className="mt-6 text-[#2D3732] leading-relaxed" data-testid="product-description">{p.description}</p>

          {tiers.length > 0 && (
            <div className="mt-6 card-farm p-4" data-testid="bulk-tiers-table">
              <div className="flex items-center gap-2 text-[#1E4D3B] font-bold mb-3"><Tag className="w-4 h-4" /> Bulk pricing</div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[#8C6D53] uppercase text-[10px] tracking-widest text-left">
                    <th className="py-1">Buy at least</th>
                    <th className="py-1 text-right">Price / {p.unit}</th>
                    <th className="py-1 text-right">You save</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className={qty >= 1 && qty < (tiers[0]?.min_qty || Infinity) ? "font-bold text-[#1E4D3B]" : ""}>
                    <td className="py-1">1+ {p.unit}</td>
                    <td className="py-1 text-right">GH₵ {p.price.toFixed(2)}</td>
                    <td className="py-1 text-right text-[#5C6660]">—</td>
                  </tr>
                  {tiers.map((t) => {
                    const nextTier = tiers.find((x) => x.min_qty > t.min_qty);
                    const active = qty >= t.min_qty && (!nextTier || qty < nextTier.min_qty);
                    return (
                      <tr key={t.min_qty} className={active ? "font-bold text-[#1E4D3B]" : ""}>
                        <td className="py-1">{t.min_qty}+ {p.unit}</td>
                        <td className="py-1 text-right">GH₵ {t.price.toFixed(2)}</td>
                        <td className="py-1 text-right text-[#1B804E]">-{Math.round((1 - t.price / p.price) * 100)}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-4 text-sm">
            <div className="inline-flex items-center gap-2 text-[#1E4D3B]"><Package className="w-4 h-4" /> {p.stock} {p.unit} in stock</div>
            {p.seller_verified && (
              <div className="inline-flex items-center gap-2 text-[#1B804E] font-semibold" data-testid="verified-badge"><ShieldCheck className="w-4 h-4" /> Verified seller</div>
            )}
          </div>

          <div className="mt-6 card-farm p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#1E4D3B] text-white flex items-center justify-center font-black">{(p.seller_name || "S")[0]}</div>
              <div className="flex-1 min-w-[140px]">
                <div className="font-bold text-[#1F2421] flex items-center gap-1.5">
                  <Store className="w-4 h-4" /> {p.seller_name}
                  {p.seller_verified && <ShieldCheck className="w-4 h-4 text-[#1B804E]" title="Verified" />}
                </div>
                <div className="text-xs text-[#5C6660]">{p.seller_region}</div>
              </div>
              {whatsappUrl && (
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-white font-semibold text-sm"
                   style={{ background: "#25D366" }}
                   data-testid="whatsapp-contact-button">
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
              )}
              {!user && (
                <Link to="/login" state={{ from: `/product/${p.id}` }}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap border border-[#1E4D3B] text-[#1E4D3B] font-semibold text-sm hover:bg-[#1E4D3B] hover:text-white transition-colors"
                      data-testid="login-to-contact-button">
                  <Lock className="w-4 h-4" /> Log in to contact
                </Link>
              )}
            </div>
          </div>

          <ShareButtons product={p} />

          <div className="mt-6 flex items-center gap-4 flex-wrap">
            <div className="flex items-center border border-[#E5D9C8] rounded-xl overflow-hidden">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-[#F3ECE2]" data-testid="qty-decrement"><Minus className="w-4 h-4" /></button>
              <div className="px-4 font-bold text-lg" data-testid="qty-display">{qty}</div>
              <button onClick={() => setQty(qty + 1)} className="p-3 hover:bg-[#F3ECE2]" data-testid="qty-increment"><Plus className="w-4 h-4" /></button>
            </div>
            <button onClick={handleAdd} className="btn-outline-forest px-5 py-3 rounded-xl font-semibold" data-testid="add-to-cart-button">Add to cart</button>
            <button onClick={handleBuy} className="btn-primary px-5 py-3 rounded-xl" data-testid="buy-now-button">Buy now</button>
            {currentPrice < p.price && (
              <div className="text-sm text-[#1B804E] font-semibold" data-testid="bulk-savings">You save GH₵ {savings}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
